/**
 * Wrapper components that embed list/detail pages within the Client layout.
 * They set URL search params so the underlying pages read clientId/referralId correctly.
 */
import React, { useEffect } from 'react';
import { useParams, useSearchParams, useOutletContext } from 'react-router-dom';
import AtPackets from '../pages/AtPackets';
import AtPacketCreate from '../pages/AtPacketCreate';
import FileReviewList from '../pages/FileReviewList';
import ProviderSearch from '../pages/ProviderSearch';
import DocGenLanding from '../pages/DocGenLanding';
import ReferralMilestones from '../pages/ReferralMilestones';
import TeamManagement from '../components/TeamManagement';
import ReferralChecklists from '../pages/ReferralChecklists';
import ClientWorkTasksPage from '../pages/ClientWorkTasks';

function ClientAtPackets() {
  const { id: clientId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  useEffect(() => {
    if (clientId && !searchParams.get('clientId')) {
      setSearchParams({ clientId }, { replace: true });
    }
  }, [clientId, searchParams, setSearchParams]);
  return <AtPackets clientIdOverride={clientId} />;
}

function ClientAtPacketCreate() {
  const { id: clientId } = useParams();
  return <AtPacketCreate clientIdOverride={clientId} returnToClientId={clientId} />;
}

function ClientFileReviews() {
  const { id: clientId } = useParams();
  return <FileReviewList clientIdOverride={clientId} />;
}

function ClientServices() {
  const { id } = useParams();
  const { currentReferral } = useOutletContext() || {};
  const [searchParams, setSearchParams] = useSearchParams();
  useEffect(() => {
    if (id) {
      const params = { clientId: id };
      if (currentReferral?.id) params.referralId = currentReferral.id;
      setSearchParams(params, { replace: true });
    }
  }, [id, currentReferral?.id, setSearchParams]);
  return (
    <ProviderSearch
      clientIdOverride={id}
      referralIdOverride={currentReferral?.id}
      defaultNeedsStatusFilter={id ? 'open' : undefined}
    />
  );
}

function ClientDocumentsAll() {
  const { id } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  useEffect(() => {
    if (id && !searchParams.get('clientId')) {
      setSearchParams({ clientId: id }, { replace: true });
    }
  }, [id, searchParams, setSearchParams]);
  return <DocGenLanding embeddedInClient />;
}

function ClientMilestones() {
  const { currentReferral, clientId } = useOutletContext() || {};
  const [searchParams] = useSearchParams();
  const queryReferralId = searchParams.get('referralId');
  if (!clientId) return null;
  if (!currentReferral?.id && !queryReferralId) {
    return <p className="client-embedded-no-referral">No referral for this client.</p>;
  }
  return <ReferralMilestones referralIdOverride={currentReferral?.id} clientIdOverride={clientId} />;
}

function ClientTeam() {
  const { currentReferral, clientId } = useOutletContext() || {};
  if (!currentReferral?.id) {
    return <p className="client-embedded-no-referral">No active referral for this client.</p>;
  }
  return <TeamManagement referralIdOverride={currentReferral.id} clientIdOverride={clientId} />;
}

function ClientChecklists() {
  const { currentReferral, clientId } = useOutletContext() || {};
  if (!currentReferral?.id) {
    return <p className="client-embedded-no-referral">No active referral for this client.</p>;
  }
  return <ReferralChecklists referralIdOverride={currentReferral.id} clientIdOverride={clientId} />;
}

function ClientWorkTasks() {
  const { currentReferral } = useOutletContext() || {};
  const [searchParams, setSearchParams] = useSearchParams();
  const referralIdFromUrl = searchParams.get('referralId') || '';
  const taskIdFromUrl = searchParams.get('taskId') || '';
  const effectiveReferralId = referralIdFromUrl || currentReferral?.id;
  if (!effectiveReferralId) {
    return <p className="client-embedded-no-referral">No active referral for this client. Work tasks are tied to the current episode of care.</p>;
  }
  return (
    <ClientWorkTasksPage
      referralIdOverride={effectiveReferralId}
      openTaskId={taskIdFromUrl || undefined}
      onOpenTaskHandled={() => {
        setSearchParams((prev) => {
          const p = new URLSearchParams(prev);
          p.delete('taskId');
          return p;
        }, { replace: true });
      }}
    />
  );
}

export {
  ClientAtPackets,
  ClientAtPacketCreate,
  ClientFileReviews,
  ClientServices,
  ClientDocumentsAll,
  ClientMilestones,
  ClientTeam,
  ClientChecklists,
  ClientWorkTasks,
};
